import { parseCarRef } from "@vms/types";
/**
 * Strip a livery/skin suffix from a resolved car label, leaving just the model.
 * Our content resolvers format labels as `"Model — skin"` (cloud) or
 * `"Model / skin"` (hub); car model names never contain those separators, so
 * the first segment is the model. Liveries are never relevant on a leaderboard.
 */
function modelName(label) {
    if (!label)
        return undefined;
    const model = label.split(/\s[—/]\s/)[0]?.trim();
    return model || undefined;
}
function distinctModels(cars) {
    return new Set(cars.map((c) => (c ? parseCarRef(c).carId : "")).filter(Boolean));
}
/**
 * Given a leaderboard's full entry list, return the car label to show per entry:
 *
 *   - The **model name** when 2+ distinct car *models* are available (liveries /
 *     skins of the same model don't count — they're compared on `carId`).
 *   - `undefined` when there's a single model, since every row is the same car
 *     and the label is just noise.
 *
 * Whether it's "multi-model" is decided by the EVENT's configured cars
 * (`availableCars`) when known — so the model shows as soon as the event *offers*
 * a second model, not only once one has actually been driven. For cross-event /
 * venue leaderboards (no single event), it falls back to the models present in
 * the results.
 *
 * Callers map by index: `car={carLabels[index]}`. One place so every leaderboard
 * (hub-ui, cloud-ui, public, venue, saved) behaves identically.
 */
export function leaderboardCarLabels(entries, availableCars) {
    const multi = availableCars && availableCars.length > 0
        ? distinctModels(availableCars).size > 1
        : distinctModels(entries.map((e) => e.car)).size > 1;
    if (!multi)
        return entries.map(() => undefined);
    return entries.map((e) => modelName(e.carName) ?? (e.car ? parseCarRef(e.car).carId : undefined));
}
//# sourceMappingURL=carLabel.js.map